import type { RefObject } from 'react';
import { fadeTo } from 'util/animation/fade/fade';
import { findTransitionController } from '../../../util/core-transition-component';
import { revealRectangle } from '../../../util/animation/reveal/reveal';

interface HeaderShowrealLargeRefs {
  elementRef?: RefObject<HTMLElement | null>;
  topTitleRef?: RefObject<HTMLHeadingElement | null>;
  bottomTitleRef?: RefObject<HTMLHeadingElement | null>;
  maskTopTitleRef?: RefObject<HTMLHeadingElement | null>;
  maskBottomTitleRef?: RefObject<HTMLHeadingElement | null>;
  siteHeaderElementRef?: RefObject<HTMLElement | null>;
  previewContainerRef?: RefObject<HTMLDivElement | null>;
}

export function setupTransitionInTimeline(
  timeline: gsap.core.Timeline,
  {
    topTitleRef,
    bottomTitleRef,
    maskTopTitleRef,
    maskBottomTitleRef,
    previewContainerRef,
  }: HeaderShowrealLargeRefs,
): void {
  const titleTopController = findTransitionController(topTitleRef);
  const maskTitleTopController = findTransitionController(maskTopTitleRef);

  const titleBottomController = findTransitionController(bottomTitleRef);
  const maskTitleBottomController = findTransitionController(maskBottomTitleRef);

  if (previewContainerRef?.current) {
    timeline.add(
      revealRectangle(previewContainerRef.current, {
        clearProps: 'clipPath, webkitClipPath',
      }),
      0,
    );
  }
  timeline.addLabel('startText', '>-0.2');

  if (titleTopController) {
    timeline.add(titleTopController.getTimeline('in') as gsap.core.Timeline, 'startText');
  }

  if (maskTitleTopController) {
    timeline.add(maskTitleTopController.getTimeline('in') as gsap.core.Timeline, 'startText');
  }

  if (titleBottomController) {
    timeline.add(titleBottomController.getTimeline('in') as gsap.core.Timeline, 'startText+=0.2');
  }

  if (maskTitleBottomController) {
    timeline.add(
      maskTitleBottomController.getTimeline('in') as gsap.core.Timeline,
      'startText+=0.2',
    );
  }
}

export function setupTransitionOutTimeline(
  timeline: gsap.core.Timeline,
  { elementRef }: HeaderShowrealLargeRefs,
): void {
  if (elementRef?.current) {
    timeline.add(fadeTo(elementRef.current as HTMLElement));
  }
}

export function enterFullScreenTransition(
  timeline: gsap.core.Timeline,
  {
    topTitleRef,
    bottomTitleRef,
    maskTopTitleRef,
    maskBottomTitleRef,
    siteHeaderElementRef,
  }: HeaderShowrealLargeRefs,
): void {
  if (maskBottomTitleRef && maskTopTitleRef && topTitleRef && bottomTitleRef) {
    timeline.add(
      fadeTo(
        [
          topTitleRef.current,
          maskTopTitleRef.current,
          maskBottomTitleRef.current,
          bottomTitleRef.current,
        ] as Array<HTMLElement>,
        {
          duration: 0.2,
        },
      ),
    );
  }

  if (siteHeaderElementRef?.current) {
    timeline.add(
      fadeTo(siteHeaderElementRef?.current, {
        duration: 0.2,
      }),
      0,
    );
  }
}

export function exitFullScreenTransition(
  timeline: gsap.core.Timeline,
  {
    topTitleRef,
    bottomTitleRef,
    maskTopTitleRef,
    maskBottomTitleRef,
    siteHeaderElementRef,
  }: HeaderShowrealLargeRefs,
): void {
  if (maskBottomTitleRef && maskTopTitleRef && topTitleRef && bottomTitleRef) {
    timeline.add(
      fadeTo(
        [
          topTitleRef.current,
          maskBottomTitleRef.current,
          maskTopTitleRef.current,
          bottomTitleRef.current,
        ] as Array<HTMLElement>,
        {
          to: 1,
          duration: 0.3,
        },
      ),
    );
  }

  if (siteHeaderElementRef?.current) {
    timeline.add(
      fadeTo(siteHeaderElementRef?.current, {
        to: 1,
        duration: 0.3,
      }),
      0,
    );
  }
}

export function scrollOutTransition(
  timeline: gsap.core.Timeline,
  {
    title,
    maskTitle,
  }: {
    title: RefObject<HTMLHeadingElement | null>;
    maskTitle: RefObject<HTMLHeadingElement | null>;
  },
): void {
  if (title && maskTitle) {
    timeline.to([title.current, maskTitle.current] as Array<HTMLElement>, {
      yPercent: -25,
    });
  }
}
